import React from "react";
import { Box, Button, Icon, Separator } from "@salestrak/ui";
import { useAuth } from "#app/lib/auth-context";
import { useAnalytics, AnalyticsContext, AuthEvent } from "#app/lib/analytics";
import { GOOGLE_CLIENT_ID, loadGoogleIdentity } from "#app/lib/google-identity";

type GoogleSignInButtonProps = {
  /** Receives a user-facing message on failure; "" clears a previous one. */
  onError: (message: string) => void;
  /**
   * Takes over what the authorization code means. Default (omitted): plain
   * sign-in/up through the auth context. The accept-invite flow passes its
   * own handler, which proves invite ownership server-side instead.
   */
  onCode?: (code: string) => void | Promise<void>;
  /** True while the surrounding form's own flow runs — disables (never spins) this button. */
  disabled?: boolean;
  /** Mirrors the Google flow (code received → handler settled) up to the form. */
  onPendingChange?: (pending: boolean) => void;
};

/**
 * Library-styled "Continue with Google" button + trailing "or" separator
 * (the separator hands off to the email/password form below it). Hidden
 * entirely when the feature is unconfigured; a script-load failure surfaces
 * as a soft error on press instead. Pressing it runs GIS's popup code flow;
 * the resulting authorization code goes through the auth context, and the
 * server merges into an existing same-email account or creates a new one.
 */
export function GoogleSignInButton({
  onError,
  onCode,
  disabled,
  onPendingChange,
}: GoogleSignInButtonProps) {
  const { signInWithGoogle } = useAuth();
  const { track } = useAnalytics();
  const [pending, setPending] = React.useState(false);

  // Warm the GIS script so the press handler usually resolves instantly.
  React.useEffect(() => {
    void loadGoogleIdentity();
  }, []);

  const handleCode = async (code: string) => {
    try {
      const { created } = await signInWithGoogle(code);
      track({
        context: AnalyticsContext.Auth,
        event: created ? AuthEvent.SignUp : AuthEvent.SignIn,
        label: "google",
      });
    } catch (err: any) {
      track({ context: AnalyticsContext.Auth, event: AuthEvent.SignIn, label: "google_failure" });
      onError(err.message ?? "Google sign-in failed. Please try again.");
    }
  };

  const handlePress = async () => {
    onError("");
    const oauth2 = await loadGoogleIdentity();
    if (!oauth2) {
      onError("Google sign-in is unavailable right now. Please try again.");
      return;
    }
    const consume = onCode ?? handleCode;
    // Pending starts when a code arrives, not on press — a dismissed popup
    // never fires the callback and must not leave a spinner behind.
    const consumeWithPending = async (code: string) => {
      setPending(true);
      onPendingChange?.(true);
      try {
        await consume(code);
      } finally {
        setPending(false);
        onPendingChange?.(false);
      }
    };
    oauth2
      .initCodeClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: "openid email profile",
        ux_mode: "popup",
        callback: (response) => void consumeWithPending(response.code),
        // Closing the popup is a change of mind, not a failure — stay quiet
        // for that one type only. Every other type (a blocked popup, an origin
        // the Google client doesn't list, an invalid client id) never opens a
        // popup and never fires `callback`, so swallowing it leaves a button
        // that looks completely inert on every press.
        error_callback: (error) => {
          if (error.type === "popup_closed") return;
          onError("Google sign-in couldn't start. Check that pop-ups are allowed, then try again.");
        },
      })
      .requestCode();
  };

  if (!GOOGLE_CLIENT_ID) return null;

  return (
    <Box direction="column" gap="xl">
      <Button
        type="button"
        variant="outline"
        loading={pending}
        disabled={disabled}
        onClick={() => void handlePress()}
      >
        <Icon name="google" size="md" />
        Continue with Google
      </Button>
      <Separator label="or" />
    </Box>
  );
}
